import { useQuery } from "react-query";
import { Parameter } from "../models";
import { ParameterType, IParameter } from "../types";
import { useCache } from "../context/CacheContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { useUserSession } from "../context/UserContext";
import { listParameters } from "../data/api/parameters";
import { queryKeys } from "../data/queryKeys";

/**
 * Configuration parameters, read through the REST API (previously a direct
 * Firestore query). The `type` filter is applied client-side to preserve the
 * previous behaviour (Firestore filtered by `type ==`). Query key, cache-key
 * invalidation and `Parameter[]` instance return shape are preserved.
 */
export const useParameters = (workspaceId: string | undefined, type: ParameterType) => {
  const { cacheKey } = useCache();
  const { activeWorkspace } = useWorkspace();
  const { userSession, isAuthenticated } = useUserSession();
  const resolvedWorkspaceId = workspaceId ?? activeWorkspace?.workspaceId ?? "";
  const token = userSession?.token?.accessToken;

  return useQuery<Parameter[], Error>(
    queryKeys.parameters(cacheKey, resolvedWorkspaceId, type.toString()),
    async () => {
      const parameters = await listParameters(token as string, resolvedWorkspaceId);
      const filtered =
        type && type !== ParameterType.All
          ? parameters.filter((parameter: IParameter) => parameter.type === type)
          : parameters;
      return filtered.map((parameter: IParameter) => new Parameter(parameter));
    },
    {
      enabled: !!token && !!resolvedWorkspaceId && isAuthenticated(),
    },
  );
};
